import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import classNames from "classnames";
import { useEffect, useRef } from "react";
import { Option } from "../types";
import styles from "./style.module.scss";

interface MetricMenuProps {
  options: Option[];
  value: string;
  onChange: (key: string) => void;
  open: boolean;
  onToggleOpen: (open: boolean) => void;
  label: string;
  className?: string;
}

/** Single-select metric picker behind a tinted pill trigger. */
function MetricMenu({
  options,
  value,
  onChange,
  open,
  onToggleOpen,
  label,
  className,
}: MetricMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        onToggleOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, onToggleOpen]);

  return (
    <div ref={rootRef} className={classNames(styles.menuWrap, className)}>
      <button
        type="button"
        aria-expanded={open}
        className={styles.menuTrigger}
        onClick={() => onToggleOpen(!open)}
      >
        {label}
        <ExpandMoreIcon className={styles.menuChevron} />
      </button>

      {open && (
        <div className={styles.menuPanel}>
          <div className={styles.menuTitle}>Select a metric</div>
          {options.map((option) => {
            const active = option.key === value;

            return (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  onChange(option.key);
                  onToggleOpen(false);
                }}
                className={classNames(
                  styles.menuOption,
                  active && styles.menuOptionActive
                )}
              >
                {active ? (
                  <RadioButtonCheckedIcon className={styles.menuRadioOn} />
                ) : (
                  <RadioButtonUncheckedIcon className={styles.menuRadioOff} />
                )}
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MetricMenu;
